/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line import/no-extraneous-dependencies
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { client } from "./api-client";

export const useGetReturnedRoutes = (data: any) =>
  useQuery<any[]>({
    queryKey: ["GetReturnedRoutes", data?.operationDate, data?.shift],
    queryFn: () => client(`field/operations/returned/routes`, { data }),
  });

export const useGetDispatchedRoutes = (data: any) =>
  useQuery<any[]>({
    queryKey: [
      "GetDispatchedRoutes",
      data?.cashCenterId,
      data?.operationDate,
      data?.shift,
    ],
    queryFn: () => client(`field/operations/dispatched/routes`, { data }),
  });

export const useDownloadDispatchDiscrepancy = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`field/operations/download/dispatch/discrepancy`, { data }),
  });

export const useGetInComingVaultHoldings = (data: any) =>
  useQuery<any[]>({
    queryKey: ["GetInComingVaultHoldings", data?.operationDate],
    queryFn: () =>
      client(`field/operations/vh/incoming/requests`, {
        data,
      }),
  });

export const useHandoverIncomingVaultHoldings = () => {
  const queryClient = useQueryClient();
  return useMutation<any[], unknown, any>({
    mutationFn: (data: any) =>
      client(`field/operations/confirm/vault/holding/handover`, { data }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ["GetInComingVaultHoldings"] });
    },
  });
};

export const useGetOutgoingVaultHoldings = () =>
  useMutation<any[], unknown, string>({
    mutationFn: (sealNo: any) =>
      client(`field/operations/vh/outgoing/requests/${sealNo}`),
  });

export const useConfirmReturnVaultHoldings = () =>
  useMutation<any[], unknown, any>({
    mutationFn: (data: any) =>
      client(`field/operations/confirm/vault/holding/return`, { data }),
  });

export const useGetSupervisors = (cashCenterId: string) =>
  useQuery<any[]>({
    queryKey: ["GetSupervisors", cashCenterId],
    queryFn: () => client(`auth/users/supervisors/${cashCenterId}`),
  });

export const useGetIncomingOrders = (data: any) =>
  useQuery<any>({
    queryKey: [
      "IncomingOrders",
      data?.cashCenterId,
      data?.operationDate,
      data?.shift,
      data?.routeNo,
    ],
    queryFn: () => client(`field/operations/incoming/boxes`, { data }),
    enabled:
      Boolean(data?.routeNo) &&
      Boolean(data?.cashCenterId) &&
      Boolean(data?.operationDate),
  });

export const useConfirmReceipt = () =>
  useMutation({
    mutationFn: (data: any) =>
      client(`field/operations/confirm/receipt`, { data }),
  });

export const useHandoverRequest = () =>
  useMutation<any[]>({
    mutationFn: (data: any) =>
      client(`field/operations/handover/consignment/boxes`, {
        data,
        successMessage: "Handover confirmed successfully!",
      }),
  });

export const useGetHandoverRequests = () =>
  useMutation<any[]>({
    mutationFn: (data: any) =>
      client(`field/operations/consignment/handover/requests`, { data }),
  });

export const useGetOutgoingOrders = (data: any) =>
  useQuery<{
    sealedPackages: any[];
    coinsBoxes: any[];
    requests?: any[];
    shift: string;
    routeNo: string;
    teamLeaderName: string;
    teamLeaderId: string;
  }>({
    queryKey: [
      "OutgoingOrders",
      data?.cashCenterId,
      data?.routeNo,
      data?.shift,
      data?.operationDate,
    ],
    queryFn: () => client(`field/operations/route/consignments`, { data }),
    enabled:
      Boolean(data?.routeNo) &&
      Boolean(data?.cashCenterId) &&
      Boolean(data?.operationDate),
  });

export const useConfirmDispatchOrders = () =>
  useMutation<any, Error, any>({
    mutationFn: (data: any) =>
      client(`field/operations/validate/consignments/discrepancy`, {
        data,
        successMessage: "Dispatch successfully!",
      }),
  });

export const useRecordDiscrepancy = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`field/operations/record/discrepancy`, { data }),
  });

export const useDownloadDiscrepancyRecord = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`field/operations/received/consignments`, { data }),
  });

export const useGetAllLocations = (data: any) =>
  useQuery<any>({
    queryKey: [
      "GetAllLocations",
      data?.cashCenterId,
      data?.pageNumber,
      data?.pageRowCount,
      data?.searchCriteria,
      data?.operationDate,
      data?.customerId,
      data?.sourceSPId,
    ],
    queryFn: () => client(`daily/operations/search/locations`, { data }),
  });

export const useGetOrdersByLocation = (barcode: string) =>
  useQuery<any[]>({
    queryKey: ["GetOrdersByLocation", barcode],
    queryFn: () => client(`daily/operations/location/services/${barcode}`),
    enabled: Boolean(barcode),
  });

export const useUpdateLocationScheduledServices = () => {
  const queryClient = useQueryClient();

  return useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`daily/operations/update/location/scheduled/services`, {
        data,
        successMessage: "Scheduled line updated successfully!",
      }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ["GetAllLocations"] });
    },
  });
};

export const useGetRostersByRoute = (data: any) =>
  useQuery<any>({
    queryKey: [
      "GetRostersByRoute",
      data?.cashCenterId,
      data?.pageNumber,
      data?.pageRowCount,
      data?.routeNo,
      data?.fromDate,
      data?.toDate,
    ],
    queryFn: () => client(`daily/operations/route/rosters`, { data }),
    enabled: Boolean(data?.routeNo),
  });

export const useGetOrdersByRoute = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`daily/operations/roster/services`, { data }),
  });

export const useGetOrderLifeCycleBySeal = (sealNo: string) =>
  useQuery<any>({
    queryKey: ["GetOrderLifeCycleBySeal", sealNo],
    queryFn: ({ signal }) =>
      client(`daily/operations/search/package/${sealNo}`, { signal }),
    enabled: Boolean(sealNo),
  });

export const useGetB2BDeliveryHoldingPackages = (data: any) =>
  useQuery<{ totalRecords: number; data: any[] }>({
    queryKey: [
      "GetB2BDeliveryHoldingPackages",
      data?.operationDate,
      data?.searchCriteria,
      data?.pageNumber,
      data?.pageRowCount,
      data?.searchCriteria,
      data?.operationDate,
      data?.cashCenterId,
      data?.customerId,
      data?.sourceSPId,
      data?.onwardSPId,
    ],
    queryFn: () =>
      client(`services/b2b/holding/delivery/jobs`, {
        data,
      }),
  });

export const useVerifyB2BDeliveryHoldingSealNo = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(
        `services/b2b/verify/delivery/${data?.deliveryDate}/${data?.sealNo}`,
      ),
  });

export const useRemoveB2BDeliveryHoldingPackage = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(
        `services/b2b/remove/package/${data?.requestId}/${data?.jobNo}/${data?.sealNo}`,
      ),
  });

export const useReRouteB2BHoldingDeliveryPackages = () => {
  const queryClient = useQueryClient();
  return useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`services/b2b/reroute/jobs`, {
        data,
        successMessage: "Packages re-routed successfully!",
      }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ["GetB2BDeliveryHoldingPackages"] });
    },
  });
};

export const useGetB2BDeliveryPartialPackages = (data: any) =>
  useQuery<{ totalRecords: number; data: any[] }>({
    queryKey: [
      "GetB2BDeliveryPartialPackages",
      data?.operationDate,
      data?.searchCriteria,
      data?.pageNumber,
      data?.pageRowCount,
      data?.searchCriteria,
      data?.operationDate,
      data?.cashCenterId,
      data?.customerId,
      data?.sourceSPId,
      data?.onwardSPId,
    ],
    queryFn: () =>
      client(`rerouting/services/partial/delivery/jobs`, {
        data,
      }),
  });

export const useVerifyDeliveryPartialSealNo = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(
        `rerouting/services/verify/partial/delivery/${data?.deliveryDate}/${data?.sealNo}/${data?.serviceType}/${data?.requestId}`,
      ),
  });

export const useRemoveB2BDeliveryPartialPackage = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(
        `rerouting/services/remove/partial/package/${data?.requestId}/${data?.jobNo}/${data?.sealNo}`,
      ),
  });

export const useReRouteB2bPartialDeliveryPackages = () => {
  const queryClient = useQueryClient();
  return useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`rerouting/services/reroute/partial/jobs`, {
        data,
        successMessage: "Packages re-routed successfully!",
      }),
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: ["GetB2BDeliveryPartialPackages"],
      });
    },
  });
};

export const useGetB2BDeliveryPendingPackages = (data: any) =>
  useQuery<{ totalRecords: number; data: any[] }>({
    queryKey: [
      "GetB2BDeliveryPendingPackages",
      data?.operationDate,
      data?.searchCriteria,
      data?.pageNumber,
      data?.pageRowCount,
      data?.searchCriteria,
      data?.operationDate,
      data?.cashCenterId,
      data?.customerId,
      data?.sourceSPId,
      data?.onwardSPId,
    ],
    queryFn: () =>
      client(`rerouting/services/pending/delivery/jobs`, {
        data,
      }),
  });

export const useVerifyB2BDeliveryPendingSealNo = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(
        `rerouting/services/verify/pending/delivery/${data?.deliveryDate}/${data?.sealNo}/${data?.serviceType}/${data?.requestId}`,
      ),
  });

export const useRemoveB2BDeliveryPendingPackage = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(
        `rerouting/services/remove/pending/package/${data?.requestId}/${data?.jobNo}/${data?.sealNo}`,
      ),
  });

export const useReRouteB2bPendingDeliveryPackages = () => {
  const queryClient = useQueryClient();
  return useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`rerouting/services/reroute/pending/jobs`, {
        data,
        successMessage: "Packages re-routed successfully!",
      }),
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: ["GetB2BDeliveryPendingPackages"],
      });
    },
  });
};

export const useGetInterbranchDeliveryPendingPackages = (data: any) =>
  useQuery<{ totalRecords: number; data: any[] }>({
    queryKey: [
      "GetInterbranchDeliveryPendingPackages",
      data?.operationDate,
      data?.searchCriteria,
      data?.pageNumber,
      data?.pageRowCount,
      data?.searchCriteria,
      data?.operationDate,
      data?.cashCenterId,
      data?.customerId,
      data?.sourceSPId,
      data?.onwardSPId,
    ],
    queryFn: () =>
      client(`rerouting/services/interbranch/delivery/jobs`, {
        data,
      }),
  });

export const useVerifyInterbranchDeliveryPendingSealNo = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(
        `rerouting/services/verify/interbranch/delivery/${data?.deliveryDate}/${data?.sealNo}/${data?.requestId}`,
      ),
  });

export const useRemoveInterbranchDeliveryPendingPackage = () =>
  useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(
        `rerouting/services/remove/interbranch/package/${data?.requestId}/${data?.jobNo}/${data?.sealNo}`,
      ),
  });

export const useReRouteInterbranchPackages = () => {
  const queryClient = useQueryClient();
  return useMutation<any, unknown, any>({
    mutationFn: (data: any) =>
      client(`rerouting/services/reroute/interbranch/jobs`, {
        data,
        successMessage: "Packages re-routed successfully!",
      }),
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: ["GetInterbranchDeliveryPendingPackages"],
      });
    },
  });
};
